import { useEffect, useMemo, useState } from "react";
import { useLocation } from "react-router-dom";
import { ArrowRight, Flame, MessageCircle } from "lucide-react";
import { api } from "@/lib/api";
import { useCart } from "@/context/CartContext";
import { ProductCard } from "@/components/ProductCard";
import { FilterBar, matchesPriceRanges } from "@/components/FilterBar";
import { Fireworks } from "@/components/Fireworks";
import { DiwaliCountdown } from "@/components/DiwaliCountdown";
import { BridalRail } from "@/components/BridalRail";
import { Seo } from "@/components/Seo";
import { SITE, abs } from "@/lib/seo";
import { sizeValue } from "@/lib/sizes";
import { colorFamily } from "@/lib/colors";

const PAGE = 48;

const SaleGrid = ({ products, onAdd }) => {
  const [sizes, setSizes] = useState([]);
  const [colors, setColors] = useState([]);
  const [prices, setPrices] = useState([]);
  const [shown, setShown] = useState(PAGE);

  const filtered = useMemo(() => {
    let l = products;
    if (sizes.length) l = l.filter((p) => (p.sizes || []).some((sz) => sizes.includes(sizeValue(sz))));
    if (colors.length) l = l.filter((p) => (p.colors || []).some((c) => colors.includes(colorFamily(c))));
    if (prices.length) l = l.filter((p) => matchesPriceRanges(p.price, prices));
    return l;
  }, [products, sizes, colors, prices]);

  useEffect(() => { setShown(PAGE); }, [sizes, colors, prices]);

  return (
    <section id="sale" className="max-w-[1400px] mx-auto px-5 sm:px-8 py-14 scroll-mt-24" data-testid="festive-rail">
      <div className="flex items-end justify-between mb-6 flex-wrap gap-3">
        <div>
          <div className="overline text-[#B84A3B] mb-2">End of Season · Final Few Pieces</div>
          <h2 className="font-display text-3xl sm:text-4xl tracking-tight">Shop the Sale</h2>
          <p className="text-sm text-[#6E6E69] mt-1">Up to 70% off — when it&apos;s gone, it&apos;s gone</p>
        </div>
        <span className="text-[0.68rem] font-mono tracking-[0.16em] uppercase text-[#6E6E69]" data-testid="festive-count">
          {filtered.length} {filtered.length === 1 ? "piece" : "pieces"}
        </span>
      </div>

      <FilterBar products={products} sizes={sizes} colors={colors} prices={prices} onSizes={setSizes} onColors={setColors} onPrices={setPrices} />

      {filtered.length === 0 ? (
        <div className="border border-dashed border-[#E5E5DF] py-16 text-center" data-testid="festive-empty">
          <p className="font-display text-2xl text-[#1A1A1A]">Nothing matches those filters</p>
          <p className="text-sm text-[#6E6E69] mt-2">Try clearing a filter — or message us and we&apos;ll find your size.</p>
          <a
            href={`https://wa.me/${SITE.whatsapp}?text=${encodeURIComponent("Hi! I'm shopping the Festive Season Sale — can you help me find my size?")}`}
            target="_blank"
            rel="noreferrer"
            className="inline-flex items-center gap-2 mt-5 bg-[#25D366] text-[#0b3d1e] px-6 py-3 text-[0.7rem] font-mono tracking-[0.16em] uppercase hover:opacity-90 transition-opacity"
          >
            <MessageCircle size={14} /> Ask on WhatsApp
          </a>
        </div>
      ) : (
        <>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-x-4 gap-y-10">
            {filtered.slice(0, shown).map((p, i) => (
              <ProductCard key={p.slug} product={p} index={i % 20} onAdd={onAdd} />
            ))}
          </div>
          {filtered.length > shown && (
            <div className="text-center mt-10">
              <button
                data-testid="festive-show-more"
                onClick={() => setShown((s) => s + PAGE)}
                className="inline-flex items-center gap-2 border border-[#1A1A1A] px-8 py-3.5 text-[0.7rem] font-mono tracking-[0.18em] uppercase hover:bg-[#1A1A1A] hover:text-[#FFFFFF] transition-colors duration-300"
              >
                Show more ({filtered.length - shown} left) <ArrowRight size={13} />
              </button>
            </div>
          )}
        </>
      )}
    </section>
  );
};

export default function FestiveSale() {
  const { addItem } = useCart();
  const { hash } = useLocation();
  const [data, setData] = useState(null);

  useEffect(() => {
    api.get("/sale/current").then((r) => setData(r.data)).catch(() => setData({ festive: [], bridal: [] }));
  }, []);

  useEffect(() => {
    if (!hash || !data) return;
    const el = document.getElementById(hash.slice(1));
    if (el) setTimeout(() => el.scrollIntoView({ behavior: "smooth", block: "start" }), 200);
  }, [hash, data]);

  const festive = data?.festive || [];
  const bridalLive = (data?.bridal?.length || 0) > 0;
  const total = festive.length + (data?.bridal?.length || 0);

  return (
    <div data-testid="festive-sale-page">
      <Seo
        title="End of Season Sale 2026 — Up to 70% Off Festive Styles"
        description="The Kurti Connection End of Season Sale: hundreds of kurtis, suits, lehengas and sarees at up to 70% off, plus bridal & guest lehengas at a flat $399. In-store in Schaumburg, IL and online across the USA — when they're gone, they're gone."
        path="/festive-sale"
        image={abs("/og-festive-sale.jpg")}
      />

      {/* Festive header with grand fireworks */}
      <section
        className="relative overflow-hidden text-[#FFFFFF]"
        style={{ background: "linear-gradient(155deg, #3D0A28 0%, #160410 55%, #0A0207 100%)" }}
        data-testid="festive-sale-header"
      >
        <div className="absolute -top-24 right-[15%] w-96 h-96 rounded-full bg-[#F5C542]/10 blur-3xl pointer-events-none" />
        <Fireworks duration={7000} />
        <div className="relative max-w-[1400px] mx-auto px-5 sm:px-8 py-16 sm:py-20 text-center">
          <div className="overline text-[#F5C542] mb-4 inline-flex items-center gap-2">
            <Flame size={13} strokeWidth={1.8} /> Diwali Week · Limited Time
          </div>
          <h1 className="font-display text-5xl sm:text-6xl lg:text-7xl tracking-tight leading-[0.95]">
            End of Season <span className="italic text-[#F5C542]">Sale</span>
          </h1>
          <p className="mt-4 text-base sm:text-lg text-[#FFFFFF]/80 font-body max-w-xl mx-auto">
            Up to <b className="text-[#F5C542] font-semibold">70% off</b> festive styles across the boutique —{" "}
            {total > 0 ? `${total} pieces` : "hundreds of pieces"}, plus bridal &amp; guest lehengas at a
            flat <b className="text-[#F5C542] font-semibold">$399</b>.
          </p>
          <div className="mt-8 flex flex-col items-center gap-3">
            <div className="text-[0.62rem] font-mono tracking-[0.2em] uppercase text-[#FFFFFF]/60">Sale ends Diwali night</div>
            <DiwaliCountdown />
          </div>
          <div className="flex flex-wrap justify-center gap-3 mt-9">
            <a
              href="#sale"
              data-testid="festive-jump-sale"
              className="inline-flex items-center gap-2 bg-[#F5C542] text-[#160410] px-6 py-3 text-[0.7rem] font-mono tracking-[0.16em] uppercase font-semibold hover:bg-[#FFFFFF] transition-colors duration-300"
            >
              Shop the Sale
            </a>
            {bridalLive && (
              <a
                href="#bridal"
                data-testid="festive-jump-bridal"
                className="inline-flex items-center gap-2 border border-[#FF5D8F]/60 text-[#FF5D8F] px-6 py-3 text-[0.7rem] font-mono tracking-[0.16em] uppercase hover:bg-[#FF5D8F] hover:text-[#160410] transition-colors duration-300"
              >
                Bridal @ $399
              </a>
            )}
          </div>
        </div>
      </section>

      {!data ? (
        <div className="max-w-[1400px] mx-auto px-5 sm:px-8 py-14">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-x-4 gap-y-10" data-testid="festive-loading">
            {Array.from({ length: 8 }).map((_, i) => (
              <div key={i} className="animate-pulse">
                <div className="aspect-[2/3] bg-[#EFEFEA]" />
                <div className="h-3 bg-[#EFEFEA] mt-3 w-3/4" />
                <div className="h-3 bg-[#EFEFEA] mt-2 w-1/3" />
              </div>
            ))}
          </div>
        </div>
      ) : (
        <>
          {festive.length > 0 && <SaleGrid products={festive} onAdd={addItem} />}
          <BridalRail products={data.bridal || []} />
        </>
      )}
    </div>
  );
}
